import { Box, useTheme } from '@mui/material'
import { createContext, useCallback, useContext, useState } from 'react'
import EmailVerificationStep from './Steps/EmailVerificationStep.component'
import SignUpForm, {
    UsersRegistrationFormData,
} from './Steps/SignUpForm.component'
import AccountCreatedStep from './Steps/AccountCreatedStep.component'
import { UsersRegistrationService } from '@midmark/users-registration-service'
import { UsersRegistrationData } from '@midmark/users-registration-service/lib/service/UsersRegistration.service.models'

export const UsersRegistrationDataContext = createContext<{
    userRegistrationData: UsersRegistrationFormData
    setUserRegistrationData: React.Dispatch<
        React.SetStateAction<UsersRegistrationFormData>
    >
    initialEmailVerificationCodeSent: boolean
    setInitialEmailVerificationCodeSent: React.Dispatch<
        React.SetStateAction<boolean>
    >
    emailVerificationCode: string
    setEmailVerificationCode: React.Dispatch<React.SetStateAction<string>>
} | null>(null)
UsersRegistrationDataContext.displayName = 'UserRegistrationDataContext'

const ERROR_TEXT =
    'useSimplePager can only be used within a UserSignupWizard component'

export const useUserRegistrationDataContext = () => {
    const context = useContext(UsersRegistrationDataContext)
    if (context == null) {
        throw new Error(ERROR_TEXT)
    }
    return context
}

export enum UserSignupWizardPages {
    profileInformation,
    emailVerification,
    companyInformation,
    accountCreated,
}

interface NavigationRule {
    page: UserSignupWizardPages
    allowedBack: UserSignupWizardPages[]
    allowedForward: UserSignupWizardPages[]
}

type UserSignupWizardFormProps = {
    redirectUri: string
}

const navigationRules: NavigationRule[] = [
    {
        page: UserSignupWizardPages.profileInformation,
        allowedBack: [],
        allowedForward: [UserSignupWizardPages.emailVerification],
    },
    {
        page: UserSignupWizardPages.emailVerification,
        allowedBack: [UserSignupWizardPages.profileInformation],
        allowedForward: [UserSignupWizardPages.accountCreated],
    },
    {
        page: UserSignupWizardPages.accountCreated,
        allowedBack: [],
        allowedForward: [],
    },
]

export default function UserSignupWizard({
    redirectUri,
}: UserSignupWizardFormProps): JSX.Element {
    const theme = useTheme()

    const [currentPage, setCurrentPage] = useState(
        UserSignupWizardPages.profileInformation,
    )
    const [
        initialEmailVerificationCodeSent,
        setInitialEmailVerificationCodeSent,
    ] = useState(false)
    const [userRegistrationData, setUserRegistrationData] = useState(
        {} as UsersRegistrationFormData,
    )
    const [emailVerificationCode, setEmailVerificationCode] = useState('')
    const userRegistrationValue = {
        userRegistrationData,
        setUserRegistrationData,
        initialEmailVerificationCodeSent,
        setInitialEmailVerificationCodeSent,
        emailVerificationCode,
        setEmailVerificationCode,
    }
    const registerUserMutation =
        UsersRegistrationService.useRegisterUserMutation()

    const nextPage = (pageRequested: UserSignupWizardPages): void => {
        if (
            navigationRules
                .find((x) => x.page === currentPage)
                ?.allowedForward.some((page) => page === pageRequested)
        ) {
            setCurrentPage(pageRequested)
        }
    }

    const previousPage = (pageRequested: UserSignupWizardPages): void => {
        if (
            navigationRules
                .find((x) => x.page === currentPage)
                ?.allowedBack.some((page) => page === pageRequested)
        ) {
            setCurrentPage(pageRequested)
        }
    }

    const registerUser = useCallback(
        (id: string, code: string) => {
            const data: UsersRegistrationData = {
                firstName: userRegistrationData.firstName,
                lastName: userRegistrationData.lastName,
                email: userRegistrationData.email,
                password: userRegistrationData.password,
                confirmPassword: userRegistrationData.confirmPassword,
                jobTitle: userRegistrationData.jobTitle,
                workPhone: userRegistrationData.workExtension
                    ? userRegistrationData.workPhone +
                      ' ext. ' +
                      userRegistrationData.workExtension
                    : userRegistrationData.workPhone,
                mobilePhone: userRegistrationData.mobilePhone,
                preferredContactMethod:
                    userRegistrationData?.preferredContactMethod?.toString() ??
                    '',
            }

            registerUserMutation
                .mutateAsync({
                    userData: data ?? {},
                    id: id,
                    code: code,
                })
                .then(() => {
                    nextPage(UserSignupWizardPages.accountCreated)
                })
                .catch((error: any) => {
                    console.log(error)
                })
        },
        [userRegistrationData, emailVerificationCode, registerUserMutation],
    )

    const handleSignUpFormSubmit = (): void => {
        nextPage(UserSignupWizardPages.emailVerification)
    }

    const handleEmailVerificationSubmit = (id: string, code: string): void => {
        registerUser(id, code)
        nextPage(UserSignupWizardPages.accountCreated)
    }

    return (
        <UsersRegistrationDataContext.Provider value={userRegistrationValue}>
            <Box
                sx={{
                    position: 'relative',
                    overflowX: 'hidden',
                    overflowY: 'hidden',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '80%',
                }}
            >
                {currentPage === UserSignupWizardPages.profileInformation && (
                    <SignUpForm
                        onSubmit={handleSignUpFormSubmit}
                        redirectUri={redirectUri}
                    />
                )}

                {currentPage === UserSignupWizardPages.emailVerification && (
                    <EmailVerificationStep
                        handleBackButton={previousPage}
                        handleForwardButton={handleEmailVerificationSubmit}
                    />
                )}

                {currentPage === UserSignupWizardPages.accountCreated && (
                    <AccountCreatedStep redirectUri={redirectUri} />
                )}
            </Box>
        </UsersRegistrationDataContext.Provider>
    )
}
